const STATUS = Object.freeze({
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  ATTENTION: 'ATTENTION',
  ERROR: 'ERROR',
  INFO: 'INFO',
  IN_PROGRESS: 'IN_PROGRESS',
  DEFAULT: 'DEFAULT',
  NEUTRAL_DARK: 'NEUTRAL_DARK',
  PRIMARY: 'PRIMARY',
});

const STATUS_COLORS = Object.freeze({
  [STATUS.SUCCESS]: Object.freeze({
    TAG_BG: '!bg-success-300',
    TAG_TEXT: '!text-black',
    TAG_BORDER: '!border-success-300',
    COUNT_BG: '!bg-success-200',
    COUNT_TEXT: '!text-success-600',
    ICON: 'text-success-500',
    CARD_BG: '!bg-success-50',
    CARD_TEXT: '!text-success-600',
    CARD_BORDER: 'border-success-200',
    SUBTEXT: 'text-success-700',
    BAR_BG: '!bg-success-300',
    BAR_TEXT: '!text-success-800',
  }),
  [STATUS.WARNING]: Object.freeze({
    TAG_BG: '!bg-yellow-300',
    TAG_TEXT: '!text-black',
    TAG_BORDER: '!border-yellow-300',
    COUNT_BG: '!bg-yellow-200',
    COUNT_TEXT: '!text-yellow-600',
    ICON: 'text-yellow-500',
    CARD_BG: '!bg-yellow-50',
    CARD_TEXT: '!text-yellow-600',
    CARD_BORDER: 'border-yellow-400',
    SUBTEXT: 'text-yellow-700',
    BAR_BG: '!bg-yellow-300',
    BAR_TEXT: '!text-yellow-800',
  }),
  [STATUS.ATTENTION]: Object.freeze({
    TAG_BG: '!bg-warning-300',
    TAG_TEXT: '!text-black',
    TAG_BORDER: '!border-warning-300',
    COUNT_BG: '!bg-warning-200',
    COUNT_TEXT: '!text-warning-600',
    ICON: 'text-warning-400',
    CARD_BG: '!bg-warning-50',
    CARD_TEXT: '!text-warning-600',
    CARD_BORDER: 'border-warning-400',
    SUBTEXT: 'text-warning-700',
    BAR_BG: '!bg-warning-300',
    BAR_TEXT: '!text-warning-800',
  }),
  [STATUS.ERROR]: Object.freeze({
    TAG_BG: '!bg-danger-300',
    TAG_TEXT: '!text-black',
    TAG_BORDER: '!border-danger-300',
    COUNT_BG: '!bg-danger-200',
    COUNT_TEXT: '!text-danger-600',
    ICON: 'text-danger-500',
    CARD_BG: '!bg-danger-50',
    CARD_TEXT: '!text-danger-600',
    CARD_BORDER: 'border-danger-300',
    SUBTEXT: 'text-danger-700',
    BAR_BG: '!bg-danger-300',
    BAR_TEXT: '!text-danger-800',
  }),
  [STATUS.INFO]: Object.freeze({
    TAG_BG: '!bg-blue-200',
    TAG_TEXT: '!text-black',
    TAG_BORDER: '!border-blue-200',
    COUNT_BG: '!bg-blue-100',
    COUNT_TEXT: '!text-blue-700',
    ICON: 'text-blue-500',
    CARD_BG: '!bg-blue-50',
    CARD_TEXT: '!text-blue-700',
    CARD_BORDER: 'border-blue-200',
    SUBTEXT: 'text-blue-700',
    BAR_BG: '!bg-blue-300',
    BAR_TEXT: '!text-blue-800',
  }),
  [STATUS.IN_PROGRESS]: Object.freeze({
    TAG_BG: '!bg-yellow-300',
    TAG_TEXT: '!text-black',
    TAG_BORDER: '!border-purple-300',
    COUNT_BG: '!bg-purple-200',
    COUNT_TEXT: '!text-purple-700',
    ICON: 'text-purple-500',
    CARD_BG: '!bg-purple-50',
    CARD_TEXT: '!text-purple-700',
    CARD_BORDER: 'border-purple-200',
    SUBTEXT: 'text-purple-700',
    BAR_BG: '!bg-purple-300',
    BAR_TEXT: '!text-purple-800',
  }),
  [STATUS.DEFAULT]: Object.freeze({
    TAG_BG: '!bg-gray-100',
    TAG_TEXT: '!text-gray-600',
    TAG_BORDER: '!border-gray-400',
    COUNT_BG: '!bg-gray-300',
    COUNT_TEXT: '!text-gray-600',
    ICON: 'text-gray-500',
    CARD_BG: '!bg-gray-50',
    CARD_TEXT: '!text-gray-600',
    CARD_BORDER: 'border-gray-300',
    SUBTEXT: 'text-gray-700',
    BAR_BG: '!bg-gray-200',
    BAR_TEXT: '!text-gray-600',
  }),
  [STATUS.NEUTRAL_DARK]: Object.freeze({
    TAG_BG: '!bg-gray-700',
    TAG_TEXT: '!text-white',
    TAG_BORDER: '!border-gray-700',
    COUNT_BG: '!bg-gray-200',
    COUNT_TEXT: '!text-gray-600',
    ICON: 'text-gray-700',
    CARD_BG: '!bg-gray-50',
    CARD_TEXT: '!text-gray-600',
    CARD_BORDER: 'border-gray-800',
    SUBTEXT: 'text-gray-700',
    BAR_BG: '!bg-gray-700',
    BAR_TEXT: '!text-white',
  }),
  [STATUS.PRIMARY]: Object.freeze({
    TAG_BG: '!bg-primary-500',
    TAG_TEXT: '!text-primary-contrast',
    TAG_BORDER: '!border-primary-500',
    COUNT_BG: '!bg-primary-100',
    COUNT_TEXT: '!text-primary-700',
    ICON: 'text-primary-500',
    CARD_BG: '!bg-primary-50',
    CARD_TEXT: '!text-primary-700',
    CARD_BORDER: 'border-primary-200',
    SUBTEXT: 'text-primary-700',
    BAR_BG: '!bg-primary-300',
    BAR_TEXT: '!text-primary-800',
  }),
});

const STATUS_ICONS = Object.freeze({
  [STATUS.SUCCESS]: 'regular-check',
  [STATUS.WARNING]: 'regular-hourglass',
  [STATUS.ATTENTION]: 'regular-circle-exclamation',
  [STATUS.ERROR]: 'regular-triangle-exclamation',
  [STATUS.INFO]: 'regular-info',
  [STATUS.IN_PROGRESS]: 'regular-hourglass',
  [STATUS.DEFAULT]: 'regular-minus',
  [STATUS.NEUTRAL_DARK]: 'regular-xmark',
  [STATUS.PRIMARY]: 'regular-info',
});

const createStatusStyling = (status) => {
  const colors = STATUS_COLORS[status];
  return Object.freeze({
    STATUS_COLOR_KEY: status,
    BACKGROUND_COLOR_CLASS: colors.TAG_BG,
    TEXT_COLOR_CLASS: colors.TAG_TEXT,
    ICON_PRIMARY: STATUS_ICONS[status],
    ICON: STATUS_ICONS[status],
    ICON_COLOR: colors.ICON,
    BACKGROUND_COLOR: `${colors.CARD_BG} ${colors.CARD_TEXT} ${colors.CARD_BORDER}`,
    TAG_COLOR: `${colors.TAG_BG} ${colors.TAG_BORDER}`,
    TAG_TEXT_COLOR: colors.TAG_TEXT,
    SUBTEXT_COLOR: colors.SUBTEXT,
  });
};

const STATUS_STYLING = Object.freeze(
  Object.fromEntries(Object.values(STATUS).map((status) => [status, createStatusStyling(status)])),
);

export { STATUS, STATUS_COLORS, STATUS_ICONS, STATUS_STYLING };
export default STATUS;
